/**
 * Revalidates the authenticated session when a sleeping/stale tab becomes
 * active again. This catches expired overnight sessions before individual
 * widgets start failing with unactionable 401s.
 */
import { getCurrentInstance, onBeforeUnmount } from 'vue';

import { isOnLoginRoute } from '@/repositories/clients/sharedTokenRefresh';

const DEFAULT_MIN_INTERVAL_MS = 60 * 1000;

function hasStoredSession(windowRef) {
  return Boolean(
    windowRef?.sessionStorage?.getItem('accessToken') ||
      windowRef?.localStorage?.getItem('refreshToken')
  );
}

function canListen(target) {
  return (
    target &&
    typeof target.addEventListener === 'function' &&
    typeof target.removeEventListener === 'function'
  );
}

/**
 * @param {{
 *   authStore: { isAuthenticated?: boolean, isFetchingUser?: boolean, isRefreshingUser?: boolean, getUserInformation: Function },
 *   documentRef?: Document,
 *   windowRef?: Window,
 *   minIntervalMs?: number,
 *   now?: () => number,
 * }} options
 */
export function createSessionWakeValidator({
  authStore,
  documentRef = typeof document === 'undefined' ? null : document,
  windowRef = typeof window === 'undefined' ? null : window,
  minIntervalMs = DEFAULT_MIN_INTERVAL_MS,
  now = () => Date.now(),
}) {
  if (!authStore || typeof authStore.getUserInformation !== 'function') {
    throw new Error('createSessionWakeValidator requires an authStore with getUserInformation');
  }

  let installed = false;
  let inFlight = null;
  let lastCheckAt = 0;

  const shouldValidate = () => {
    if (documentRef?.hidden === true) return false;
    if (isOnLoginRoute()) return false;
    if (authStore.isFetchingUser || authStore.isRefreshingUser) return false;
    return authStore.isAuthenticated || hasStoredSession(windowRef);
  };

  const validate = ({ force = false } = {}) => {
    if (inFlight) return inFlight;
    if (!shouldValidate()) return Promise.resolve(false);

    const currentTime = now();
    if (
      !force &&
      lastCheckAt > 0 &&
      currentTime - lastCheckAt >= 0 &&
      currentTime - lastCheckAt < minIntervalMs
    ) {
      return Promise.resolve(false);
    }

    lastCheckAt = currentTime;
    inFlight = Promise.resolve(authStore.getUserInformation({ silent: true }))
      .catch(() => false)
      .finally(() => {
        inFlight = null;
      });

    return inFlight;
  };

  const validateFromWakeEvent = () => {
    validate().catch(() => {});
  };

  const validateWhenVisible = () => {
    if (documentRef?.hidden === true) return;
    validateFromWakeEvent();
  };

  const stop = () => {
    if (!installed) return;
    installed = false;
    if (canListen(documentRef)) {
      documentRef.removeEventListener('visibilitychange', validateWhenVisible);
    }
    if (canListen(windowRef)) {
      windowRef.removeEventListener('focus', validateFromWakeEvent);
      windowRef.removeEventListener('online', validateFromWakeEvent);
      windowRef.removeEventListener('pageshow', validateWhenVisible);
    }
  };

  const install = () => {
    if (installed) return stop;
    installed = true;
    if (canListen(documentRef)) {
      documentRef.addEventListener('visibilitychange', validateWhenVisible);
    }
    if (canListen(windowRef)) {
      windowRef.addEventListener('focus', validateFromWakeEvent);
      windowRef.addEventListener('online', validateFromWakeEvent);
      windowRef.addEventListener('pageshow', validateWhenVisible);
    }
    return stop;
  };

  return { install, stop, validate };
}

export function useSessionWakeValidation(authStore, options = {}) {
  const { disabled = false, ...validatorOptions } = options;
  const validator = createSessionWakeValidator({ authStore, ...validatorOptions });

  if (!disabled && getCurrentInstance()) {
    validator.install();
    onBeforeUnmount(validator.stop);
  }

  return validator;
}
